'use client'
import { Icon } from '@/icons'
import cn from 'classnames'
import { FunctionComponent, useCallback, useMemo } from 'react'

import { Button } from '@/components/Button'
import { ButtonLink } from '@/components/ButtonLink'

import {
  BRANDSIDE_SERVICE_URL,
  YANDEX_METRIKA,
  YANDEX_METRIKA_COUNTER,
} from '@/config'
import { scrollTop } from '@/utils/DOM'

import { orderCallPopupStore } from '@/store/OrderCallPopup.store'
import { useMarketingUrl } from '@/hooks/MarketingUrl'
import { formatPhone } from '@/utils/string'
import Link from 'next/link'
import styles from './Footer.module.scss'

type FooterProps = {
  phone: string
  telegram: string
  whatsapp: string
  max: string
}
export const Footer: FunctionComponent<FooterProps> = ({
  phone,
  telegram,
  whatsapp,
  max,
}) => {
  const formattedPhone = useMemo(() => formatPhone(phone), [phone])
  const marketingUrl = useMarketingUrl()

  const orderCallClick = useCallback(() => {
    if (YANDEX_METRIKA) {
      // @ts-ignore
      ym(YANDEX_METRIKA_COUNTER, 'reachGoal', 'BUTTON_ORDER_CALL_CLICK')
    }
    orderCallPopupStore.show()
  }, [])

  const currentYear = useMemo(() => {
    return new Date().getFullYear()
  }, [])

  return (
    <footer className={cn(styles.container, styles.dark)}>
      <div className={styles.content}>
        <div className={styles.top}>
          <div className={styles.logoColumn}>
            <Link
              href="/"
              className={styles.logoLink}
              aria-label="Главная страница"
            >
              <Icon name="logo" className={styles.logo} />
            </Link>
            <div className={cn(styles.contacts, styles.contactsLogo)}>
              <div className={styles.contactsLinks}>
                <a href={`tel:${phone}`} className={styles.phone}>
                  {formattedPhone}
                </a>

                {/* TODO: Доделать вывод почты */}
                <a href={`mailto:info@brandside.ru`} className={styles.phone}>
                  info@brandside.ru
                </a>
              </div>
              <div className={styles.socials}>
                <a
                  href={telegram}
                  className={styles.soc}
                  target="_blank"
                  aria-label="Телеграм"
                >
                  <div className={styles.socBackground}></div>
                  <Icon name="telegram" className={styles.socIcon} />
                </a>
                <a
                  href={whatsapp}
                  className={styles.soc}
                  target="_blank"
                  aria-label="Whatsapp"
                >
                  <div className={styles.socBackground}></div>
                  <Icon name="whatsapp" className={styles.socIcon} />
                </a>
                <a
                  href={max}
                  className={styles.soc}
                  target="_blank"
                  aria-label="Max"
                >
                  <div className={styles.socBackground}></div>
                  <Icon name="max" className={styles.socIcon} />
                </a>
              </div>
              <p className={styles.address}>
                <Icon name={'pinMap'} className={styles.addressPin} />
                <span>
                  Москва, Садовая-Спасская ул., 1/2к4<br></br>
                  Пн-Пт, с 10:00 до 19:00
                </span>
              </p>
            </div>
          </div>
          <div className={cn(styles.contacts, styles.contactsColumn)}>
            <div className={styles.contactsLinks}>
              <a href={`tel:${phone}`} className={styles.phone}>
                {formattedPhone}
              </a>

              {/*
            TODO: Доделать вывод почты
            */}
              <a href={`mailto:info@brandside.ru`} className={styles.phone}>
                info@brandside.ru
              </a>
            </div>
            <div className={styles.socials}>
              <a href={telegram} className={styles.soc} target="_blank">
                <div className={styles.socBackground}></div>
                <Icon name="telegram" className={styles.socIcon} />
              </a>
              <a href={whatsapp} className={styles.soc} target="_blank">
                <div className={styles.socBackground}></div>
                <Icon name="whatsapp" className={styles.socIcon} />
              </a>
              <a href={max} className={styles.soc} target="_blank">
                <div className={styles.socBackground}></div>
                <Icon name="max" className={styles.socIcon} />
              </a>
            </div>
            <p className={styles.address}>
              <Icon name="pinMap" className={styles.addressPin} />
              <span>
                Москва, Садовая-Спасская ул., 1/2к4<br></br>
                Пн-Пт, с 10:00 до 19:00
              </span>
            </p>
          </div>

          <div className={styles.buttons}>
            <ButtonLink
              className={cn(styles.buttonAccount)}
              href={marketingUrl(BRANDSIDE_SERVICE_URL ?? '')}
              target="_blank"
            >
              <Icon name="user" className={styles.userIcon} />
              Личный кабинет
            </ButtonLink>
            <Button
              className={styles.buttonCall}
              variant={'opacity'}
              type="button"
              onClick={orderCallClick}
            >
              Заказать звонок
            </Button>
          </div>

          <nav className={styles.nav}>
            <Link href="/tools" className={styles.link}>
              Сервисы
            </Link>{' '}
            <Link href="/uslugi" className={styles.link}>
              Услуги
            </Link>{' '}
            <Link href="/o-kompanii" className={styles.link}>
              О нас
            </Link>{' '}
            <Link href="/blog" className={styles.link}>
              Блог
            </Link>{' '}
            <Link href="/contacts" className={styles.link}>
              Контакты
            </Link>{' '}
            <Link href="/documents" className={styles.link}>
              Документы
            </Link>{' '}
            <Link href="/partners" className={styles.link}>
              Партнеры
            </Link>{' '}
            <Link href="/sitemap" className={styles.link}>
              Карта сайта
            </Link>
          </nav>
        </div>
        <div className={styles.bottom}>
          <div className={styles.bottomContent}>
            <p className={styles.copyright}>
              © {currentYear} &nbsp;ООО «БРЕНДСАЙД»<br></br> ОГРН:
              1217700505967 &nbsp;&nbsp; ИНН: 9721147550
            </p>
            <div className={styles.politicLinks}>
              <a
                href="/documents/policy"
                className={styles.politic}
                target="_blank"
              >
                Политика обработки персональных данных
              </a>
              <a
                href="/documents/user-agreement"
                className={styles.politic}
                target="_blank"
              >
                Пользовательское соглашение
              </a>
            </div>
            <a
              href="https://www.altera-media.com/?utm_source=clnt"
              className={styles.politic}
              target="_blank"
            >
              Продвижение сайта - Альтера
            </a>
          </div>

          <Button
            variant="opacity"
            className={styles.arrowUp}
            onClick={scrollTop}
            ariaLabel="Наверх"
          >
            <Icon name="arrowUp" className={styles.arrowUpIcon} />
          </Button>
        </div>
      </div>
    </footer>
  )
}
